/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow

export type ExtractionRowState = 'done' | 'active' | 'pending';

export type ExtractionRow = {|
  key: string,
  heading: string,
  state: ExtractionRowState,
  meta: ?string,
|};

export type ExtractionProgressView = {|
  percent: number,
  stageLabel: string,
  detail: ?string,
  rows: Array<ExtractionRow>,
  isWorking: boolean,
|};

export const STAGE_LABEL = {
  EXTRACTION_STARTED: 'Getting started…',
  PREPROCESSING: 'Reading the report…',
  EXTRACTING: 'Finding model inputs, one section at a time…',
  VALIDATING: 'Checking the proposed inputs…',
  PROPOSAL_READY: 'Done. Taking you to the model to review…',
  UNKNOWN: 'Still working…',
};

// Honest floors, ordered so the percent never moves backwards within one run
// (2 < 8 < 15..85 < 90 < 100). PREPROCESSING is one synchronous PDF read (seconds); the section
// passes are the minutes, so they own the widest band and advance linearly in COMPLETED passes,
// the only sub-signal the worker reports.
export const STAGE_PERCENT = {
  EXTRACTION_STARTED: 2,
  PREPROCESSING: 8,
  EXTRACTING_FROM: 15,
  EXTRACTING_SPAN: 70,
  VALIDATING: 90,
  PROPOSAL_READY: 100,
};

const READ_HEADING = 'Read the report';
const FIND_HEADING = 'Find model inputs';
const CHECK_HEADING = 'Check the proposed inputs';

// Every count is untrusted (an older row, a mocked payload): anything but a non-negative integer
// counts as zero.
const count = (n: mixed): number =>
  typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : 0;

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const row = (
  key: string,
  heading: string,
  state: ExtractionRowState,
  meta: ?string = null
): ExtractionRow => ({ key, heading, state, meta });

/*
 * The progress page's view model: the latest job payload -> what to show. A pure function of ONE
 * payload, keyed on `status` only (`progress.stage` is left stale by the sink and the DLQ
 * consumer). It holds no memory of earlier polls, so a redelivered job that restarts from the top
 * is shown honestly, and a payload missing its counts degrades to the stage and the three steps.
 *
 * The three steps and the "n of N sections read" count come from what the worker reports today
 * (pagesTotal, sectionsTotal, sectionsDone). Naming each section as it is read needs the worker
 * to report the section list; that is a separate change.
 */
const describeExtractionProgress = ({
  status,
  progress,
}: {
  status: string,
  progress?: ?{ [string]: mixed },
}): ExtractionProgressView => {
  const p = progress && typeof progress === 'object' ? progress : {};
  const total = count(p.sectionsTotal);
  const done = Math.min(count(p.sectionsDone), total);
  const pagesTotal = count(p.pagesTotal);
  const detail = total > 0 ? `${done} of ${plural(total, 'section')} read` : null;

  const readRow = (state: ExtractionRowState) =>
    row('read', READ_HEADING, state, pagesTotal > 0 ? plural(pagesTotal, 'page') : null);
  const findRow = (state: ExtractionRowState) => row('find', FIND_HEADING, state);
  const checkRow = (state: ExtractionRowState) => row('check', CHECK_HEADING, state);

  switch (status) {
    case 'EXTRACTION_STARTED':
    case 'PREPROCESSING':
      return {
        percent: STAGE_PERCENT[status],
        stageLabel: STAGE_LABEL[status],
        detail: null,
        rows: [readRow('active'), findRow('pending'), checkRow('pending')],
        isWorking: true,
      };
    case 'EXTRACTING':
      return {
        percent:
          total > 0
            ? STAGE_PERCENT.EXTRACTING_FROM +
              Math.round((STAGE_PERCENT.EXTRACTING_SPAN * done) / total)
            : STAGE_PERCENT.EXTRACTING_FROM,
        stageLabel: STAGE_LABEL.EXTRACTING,
        detail,
        rows: [readRow('done'), findRow('active'), checkRow('pending')],
        isWorking: true,
      };
    case 'VALIDATING':
    case 'PROPOSAL_READY': {
      const ready = status === 'PROPOSAL_READY';
      return {
        percent: STAGE_PERCENT[status],
        stageLabel: STAGE_LABEL[status],
        detail,
        rows: [readRow('done'), findRow('done'), checkRow(ready ? 'done' : 'active')],
        isWorking: !ready,
      };
    }
    default:
      // Terminal errors never reach here (the page shows the reason instead); this is a status the
      // UI does not know yet.
      return {
        percent: STAGE_PERCENT.EXTRACTION_STARTED,
        stageLabel: STAGE_LABEL.UNKNOWN,
        detail: null,
        rows: [],
        isWorking: true,
      };
  }
};

export default describeExtractionProgress;
